/**
 * Compact quote form, shown inside the hero on every page.
 *
 * The brief is explicit that the quote action must not be something customers
 * hunt for, so this sits above the fold rather than waiting on the contact
 * page. It is deliberately short — five fields — because every extra field on
 * a first-contact form costs completions, and everything else (property size,
 * condition, dates, photos) is easier to gather in the reply.
 *
 * It posts to the same endpoint as the full contact form, so enquiries from
 * here land in Quote Requests alongside the rest.
 */
import { useState } from "react";
import { Loader2, CheckCircle2, ShieldCheck, User, Phone, Mail, MapPin } from "lucide-react";
import { useServices } from "../../context/ServicesContext";
import { enquiryService } from "../../services/enquiryService";

const EMPTY = {
  service: "",
  name: "",
  phone: "",
  email: "",
  location: "",
  message: "",
  // Honeypot — hidden from real users. See functions/src/schemas/enquiries.ts.
  website: "",
};

export default function HeroQuoteForm({
  defaultService = "",
  heading = "Get Your Free Quote",
  showMessage = false,
}) {
  const { services } = useServices();
  const [form, setForm] = useState({ ...EMPTY, service: defaultService });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [failure, setFailure] = useState(null);

  const set = (key) => (event) => {
    setForm((current) => ({ ...current, [key]: event.target.value }));
    if (errors[key]) setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setFailure(null);

    const next = {};
    if (!form.service) next.service = "Choose a service.";
    if (!form.name.trim()) next.name = "Enter your name.";
    if (form.phone.replace(/[^\d]/g, "").length < 8) next.phone = "Enter a valid number.";
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSubmitting(true);
    const result = await enquiryService.submit(form);
    setSubmitting(false);

    if (result.ok) {
      setSent(true);
      setForm({ ...EMPTY, service: defaultService });
      return;
    }
    if (result.fields) setErrors(result.fields);
    setFailure(result.message);
  };

  if (sent) {
    return (
      <div
        className="bg-ink/95 backdrop-blur border border-emerald/40 rounded-lg p-7 text-center"
        data-testid="hero-quote-success"
        role="status"
      >
        <CheckCircle2 className="w-10 h-10 text-emerald mx-auto" aria-hidden="true" />
        <h3 className="mt-4 font-display font-bold text-white text-lg">Request received</h3>
        <p className="mt-2 text-sm text-mutedtext leading-relaxed">
          We&rsquo;ll come back to you with a quotation, usually within one working day.
        </p>
        <button
          type="button"
          onClick={() => setSent(false)}
          className="mt-5 text-xs uppercase tracking-[0.16em] text-emerald hover:text-white"
        >
          Send another
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      noValidate
      data-testid="hero-quote-form"
      className="relative bg-ink/95 backdrop-blur border border-charcoal rounded-lg p-6 md:p-7 shadow-2xl"
    >
      <h2 className="font-display font-extrabold text-white text-xl md:text-2xl text-center">
        {heading}
      </h2>

      {failure && (
        <p className="mt-4 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">
          {failure}
        </p>
      )}

      <div className="mt-5 space-y-3">
        <Row error={errors.service}>
          <select
            value={form.service}
            onChange={set("service")}
            aria-label="Select a service"
            data-testid="hero-service"
            className={fieldCls(errors.service, true)}
          >
            <option value="">Select a Service</option>
            {services.map((service) => (
              <option key={service.slug} value={service.name}>
                {service.name}
              </option>
            ))}
          </select>
        </Row>

        <Row icon={User} error={errors.name}>
          <input
            type="text"
            value={form.name}
            onChange={set("name")}
            placeholder="Name"
            autoComplete="name"
            aria-label="Your name"
            data-testid="hero-name"
            className={fieldCls(errors.name)}
          />
        </Row>

        <Row icon={Phone} error={errors.phone}>
          <input
            type="tel"
            value={form.phone}
            onChange={set("phone")}
            placeholder="Phone Number"
            autoComplete="tel"
            inputMode="tel"
            aria-label="Phone number"
            data-testid="hero-phone"
            className={fieldCls(errors.phone)}
          />
        </Row>

        <Row icon={Mail} error={errors.email}>
          <input
            type="email"
            value={form.email}
            onChange={set("email")}
            placeholder="Email (Optional)"
            autoComplete="email"
            aria-label="Email address, optional"
            className={fieldCls(errors.email)}
          />
        </Row>

        <Row icon={MapPin} error={errors.location}>
          <input
            type="text"
            value={form.location}
            onChange={set("location")}
            placeholder="Address / Area"
            aria-label="Address or area"
            className={fieldCls(errors.location)}
          />
        </Row>

        {showMessage && (
          <Row error={errors.message}>
            <textarea
              rows={4}
              value={form.message}
              onChange={set("message")}
              placeholder="Tell us more — property size, condition, anything else"
              aria-label="Additional details"
              className={fieldCls(errors.message, true)}
            />
          </Row>
        )}
      </div>

      {/*
        Honeypot. Hidden from people, tempting to a bot. Positioned off-screen
        rather than display:none, which some bots know to skip.
      */}
      <div className="absolute left-[-9999px] top-0" aria-hidden="true">
        <label>
          Website
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={set("website")}
          />
        </label>
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid="hero-quote-submit"
        className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded bg-emerald text-ink px-6 py-3.5 font-semibold text-sm hover:bg-emerald-light disabled:opacity-60 transition-colors"
      >
        {submitting && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
        {submitting ? "Sending…" : "Request Quote"}
      </button>

      <p className="mt-4 flex items-center justify-center gap-2 text-[11px] text-mutedtext">
        <ShieldCheck className="w-3.5 h-3.5 text-emerald" aria-hidden="true" />
        Your details are only used to prepare your quotation.
      </p>
    </form>
  );
}

/** One field row: optional leading icon, the control, and any error beneath. */
function Row({ icon: Icon, error, children }) {
  return (
    <div>
      <div className="relative">
        {Icon && (
          <Icon
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-mutedtext pointer-events-none z-10"
            aria-hidden="true"
          />
        )}
        {children}
      </div>
      {error && <p className="mt-1 text-[11px] text-red-400">{error}</p>}
    </div>
  );
}

const fieldCls = (error, isSelect = false) =>
  [
    "w-full border rounded text-sm text-white placeholder:text-mutedtext/70",
    "px-3 py-3 outline-none transition-colors",
    isSelect ? "cursor-pointer bg-ink" : "pl-10 bg-charcoal/30",
    error ? "border-red-500/70 focus:border-red-500" : "border-charcoal focus:border-emerald",
  ].join(" ");
